/**
 * Pure helpers for positional paste/attachment anchors. Single source of truth
 * for the anchor token format, the attachment-type → anchor-kind mapping, and
 * the reconstruction walk. No React, no DOM — fully unit-testable.
 */
import type {
  Attachment,
  AttachmentType,
  PromptAttachment,
  StudyDocumentAttachment,
} from './types';

/** All attachment variants that carry an `anchorId` (excludes prompt and study-document). */
type AnchoredAttachment = Exclude<Attachment, PromptAttachment | StudyDocumentAttachment>;

/** Anchor kinds embedded in tokens. Note: NOT 1:1 with AttachmentType. */
export type AnchorKind = 'paste' | 'image' | 'file';

/** Returns a fresh global anchor-matching regex (capturing kind + hyphen-free anchorId).
 *  A factory, not a shared constant, so callers never trip over a stale `lastIndex`. */
export function anchorRegex(): RegExp {
  return /⟦(paste|image|file):([a-z0-9]+)⟧/g;
}

/** Maps an attachment type to its anchor kind, or null for non-anchored kinds (prompt, study-document). */
export function kindForAttachment(type: AttachmentType): AnchorKind | null {
  switch (type) {
    case 'pasted-text':
      return 'paste';
    case 'image':
      return 'image';
    case 'document':
    case 'text-file':
      return 'file';
    default:
      return null; // 'prompt', 'study-document'
  }
}

/** Generates a short, hyphen-free, non-guessable id from the regex charset [a-z0-9]. */
export function makeAnchorId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10).toLowerCase();
}

/** Builds the anchor token string for a kind + id. */
export function buildAnchor(kind: AnchorKind, anchorId: string): string {
  return `⟦${kind}:${anchorId}⟧`;
}

/**
 * Replaces the selected range [start, end) in `value` with `anchor` (a paste over
 * a selection overwrites it). Returns the new value and the caret position after
 * the anchor.
 * Caller must pass in-range offsets (0 ≤ selectionStart ≤ selectionEnd ≤ value.length);
 * out-of-range indices are silently clamped by slice().
 */
export function insertAnchorAtCursor(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  anchor: string
): { value: string; caret: number } {
  const next = value.slice(0, selectionStart) + anchor + value.slice(selectionEnd);
  return { value: next, caret: selectionStart + anchor.length };
}

/** Removes every occurrence of the exact `⟦kind:anchorId⟧` token. */
export function stripAnchor(value: string, kind: AnchorKind, anchorId: string): string {
  return value.split(buildAnchor(kind, anchorId)).join('');
}

/** Result of a reconstruction pass. */
export interface Reconstructed {
  /** Text with resolved anchors spliced in place; unresolved tokens left literal. */
  inlineContent: string;
  /** Fenced/markered blocks for attachments not spliced during the walk. */
  unanchoredBlocks: string[];
  /** File attachments re-sequenced for upload (PR1: manifest order, unchanged). */
  orderedFiles: Attachment[];
}

// 8 random chars makes accidental collision astronomically unlikely; the loop is capped regardless.
const FENCE_NONCE_LEN = 8;

/**
 * Wraps pasted-text content in a fenced block, hardening the tag with a nonce when
 * the content contains the bare close delimiter so the boundary can't be forged.
 */
function fencePastedText(content: string, index: number): string {
  let tag = 'pasted_text';
  // First iteration succeeds for any real-world content; cap guarantees termination
  // even against adversarial self-referential pastes.
  for (let i = 0; i < 5 && content.includes(`</${tag}>`); i++) {
    tag = `pasted_text_${makeAnchorId().slice(0, FENCE_NONCE_LEN)}`;
  }
  return `<${tag} id="${index}">\n${content}\n</${tag}>`;
}

/** Returns the display word for a file attachment type in a file marker. */
function fileWord(type: AttachmentType): string {
  if (type === 'image') return 'image';
  if (type === 'document') return 'document';
  return 'file'; // text-file
}

/** Builds a trailing/inline file reference marker. Index is 1-based index among FILE chips (excludes pasted-text and prompt), by manifest order. */
function fileMarker(att: Attachment, index: number): string {
  return `[Attached ${fileWord(att.type)} ${index}: ${att.name}]`;
}

/**
 * Builds the fallback (unanchored) blocks for any non-prompt attachment not consumed
 * during the walk, in manifest order.
 */
function buildUnanchoredBlocks(
  attachments: Attachment[],
  consumed: Set<string>,
  pasteIndexOf: Map<string, number>,
  fileIndexOf: Map<string, number>,
): string[] {
  const blocks: string[] = [];
  for (const a of attachments) {
    // Neither carries text for the message body: a prompt folds in its
    // instructions elsewhere, and a study-document is read by the server.
    if (a.type === 'prompt' || a.type === 'study-document') continue;
    const anchored = a as AnchoredAttachment;
    const aid = anchored.anchorId;
    if (consumed.has(aid)) continue;
    if (a.type === 'pasted-text') {
      // safe: pasteIndexOf is built from pasted array which owns this anchor
      blocks.push(fencePastedText(a.content, pasteIndexOf.get(aid)!));
    } else {
      // safe: fileIndexOf is built from files array which owns this anchor
      blocks.push(fileMarker(a, fileIndexOf.get(aid)!));
    }
  }
  return blocks;
}

/**
 * Reconstructs the message body by splicing each resolved anchor in place and
 * collecting fallback blocks for anything not spliced. Resolution is against the
 * live `attachments` list only; any token that does not resolve (missing id, kind
 * mismatch, duplicate, or user-typed foreign token) is left as literal text — never
 * stripped. `orderedFiles` = consumed files in walk order, then unconsumed in manifest
 * order. `prompt` attachments are excluded entirely.
 */
export function reconstructContent(text: string, attachments: Attachment[]): Reconstructed {
  const pasted = attachments.filter(
    (a): a is Extract<Attachment, { type: 'pasted-text' }> => a.type === 'pasted-text'
  );
  // A study-document is a reference the server reads, never an upload.
  const files = attachments.filter(
    (a): a is AnchoredAttachment =>
      a.type !== 'pasted-text' && a.type !== 'prompt' && a.type !== 'study-document'
  );

  // 1-based indices for display, keyed by anchorId, per category.
  const pasteIndexOf = new Map(pasted.map((a, i) => [a.anchorId, i + 1]));
  const pasteById = new Map(pasted.map(a => [a.anchorId, a]));
  const fileIndexOf = new Map(files.map((a, i) => [a.anchorId, i + 1]));
  const fileById = new Map(files.map(a => [a.anchorId, a]));

  const consumed = new Set<string>();
  const consumedOrder: string[] = []; // anchorIds in walk order, for files only

  const re = anchorRegex();
  const inlineContent = text.replace(re, (token, kind: string, anchorId: string) => {
    if (consumed.has(anchorId)) return token; // duplicate → leave literal

    if (kind === 'paste') {
      const chip = pasteById.get(anchorId);
      if (!chip) return token;
      consumed.add(anchorId);
      // safe: pasteIndexOf and pasteById are built from the same array
      return fencePastedText(chip.content, pasteIndexOf.get(anchorId)!);
    }

    if (kind === 'image' || kind === 'file') {
      const att = fileById.get(anchorId);
      if (!att || kindForAttachment(att.type) !== kind) return token;
      consumed.add(anchorId);
      consumedOrder.push(anchorId);
      // safe: fileIndexOf and fileById are built from the same array
      return fileMarker(att, fileIndexOf.get(anchorId)!);
    }

    return token; // unknown kind → leave literal
  });

  const consumedSet = new Set(consumedOrder);
  const orderedFiles = [
    ...consumedOrder.map(id => fileById.get(id)!),
    ...files.filter(a => !consumedSet.has(a.anchorId)),
  ];

  const unanchoredBlocks = buildUnanchoredBlocks(attachments, consumed, pasteIndexOf, fileIndexOf);

  return { inlineContent, unanchoredBlocks, orderedFiles };
}
